import { useEffect, useState } from "react";
import { ArrowLeft, Crown, Loader2, RefreshCw, TrendingUp } from "lucide-react";
import { api } from "@/api/client";

// Founder-only Intent Dashboard — ranks the 3 creator tracks by upgrade votes
// pulled from misteck_user_intent_logs, so we know what to build next.

const MEDALS = ["🥇", "🥈", "🥉"];
const BARS = ["#FFCC00", "#98FFB6", "#A4DCFA"];

export default function IntentDashboard({ onBack }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  const load = async () => {
    setLoading(true); setErr(null);
    try {
      const r = await api.get("/cockpit/user-intent-summary");
      setData(r.data);
    } catch (e) {
      setErr(e?.response?.status === 403 ? "Founder access only." : "Couldn't load intent data.");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const tracks = data?.tracks || [];
  const total = data?.total_votes || 0;
  const leader = tracks[0];

  return (
    <div className="absolute inset-0 z-50 flex flex-col bg-cream" data-testid="intent-dashboard">
      {/* Header */}
      <div className="flex items-center gap-2 border-b-2 border-black bg-black px-4 py-3">
        <button onClick={onBack} data-testid="intent-back" className="grid h-9 w-9 place-items-center border-2 border-[#FFCC00] bg-black text-[#FFCC00] active:scale-95">
          <ArrowLeft size={16} />
        </button>
        <div className="leading-tight flex-1">
          <p className="font-mono text-[9px] uppercase tracking-[0.24em] text-[#FFCC00]">Cockpit · Telemetry</p>
          <h2 className="font-serif text-xl text-white">Intent Dashboard</h2>
        </div>
        <button onClick={load} data-testid="intent-refresh" className="grid h-9 w-9 place-items-center border-2 border-[#FFCC00] bg-black text-[#FFCC00] active:scale-95" aria-label="Refresh">
          <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto no-scrollbar p-4 space-y-4">
        {loading ? (
          <div className="grid place-items-center py-16" data-testid="intent-loading"><Loader2 size={26} className="animate-spin text-black/50" /></div>
        ) : err ? (
          <div className="border-2 border-black bg-[#FF6B6B] p-4 text-center shadow-[3px_3px_0px_#000]" data-testid="intent-error">
            <p className="font-mono text-[11px] font-black uppercase tracking-widest text-black">{err}</p>
          </div>
        ) : total === 0 ? (
          <div className="border-2 border-black bg-white p-6 text-center shadow-[3px_3px_0px_#000]" data-testid="intent-empty">
            <p className="font-mono text-[11px] font-black uppercase tracking-widest text-black/50">No votes yet — users haven't upgraded any tracks.</p>
          </div>
        ) : (
          <>
            {/* Total votes */}
            <div className="flex items-center gap-2 border-2 border-black bg-black px-3 py-2 shadow-[3px_3px_0px_#000]">
              <TrendingUp size={15} className="text-[#FFCC00]" />
              <p className="font-mono text-[10px] font-black uppercase tracking-widest text-white">Total community votes</p>
              <span className="ml-auto font-mono text-2xl font-black text-[#FFCC00]" data-testid="intent-total">{total}</span>
            </div>

            {/* Leaderboard */}
            <div className="space-y-3" data-testid="intent-leaderboard">
              {tracks.map((t, i) => (
                <div key={t.track} data-testid={`intent-rank-${i + 1}`}
                  className={`border-2 border-black p-3.5 shadow-[4px_4px_0px_#000] ${i === 0 ? "bg-[#FFCC00]" : "bg-white"}`}>
                  <div className="flex items-center gap-2.5">
                    <span className="text-2xl leading-none" aria-hidden>{MEDALS[i] || "•"}</span>
                    {i === 0 && <Crown size={18} className="text-black" data-testid="intent-crown" />}
                    <div className="flex-1 min-w-0 leading-tight">
                      <p className="font-serif text-lg text-black truncate" data-testid={`intent-name-${i + 1}`}>#{i + 1} {t.name}</p>
                      <p className="font-mono text-[9px] font-bold uppercase tracking-widest text-black/60">
                        <span data-testid={`intent-votes-${i + 1}`}>{t.votes}</span> community votes (<span data-testid={`intent-pct-${i + 1}`}>{t.percentage}</span>%)
                      </p>
                    </div>
                  </div>
                  <div className="mt-2.5 h-3 w-full border-2 border-black bg-white overflow-hidden">
                    <div className="h-full transition-[width] duration-500" style={{ width: `${t.percentage}%`, background: BARS[i] || "#ddd" }} />
                  </div>
                </div>
              ))}
            </div>

            {/* Recommendation */}
            {leader && (
              <div className="border-2 border-black bg-[#98FFB6] p-4 shadow-[4px_4px_0px_#000]" data-testid="intent-recommendation">
                <p className="font-mono text-[9px] font-black uppercase tracking-widest text-black/60 mb-1">Recommendation</p>
                <p className="font-sans text-sm font-bold text-black leading-snug">
                  Your users are heavily prioritizing <b>{leader.name}</b>. Allocate your next engineering development sprint to this feature set.
                </p>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
